import type { Locale } from '@change-corgi/config/locales';
import type { UtilityContext } from '@change-corgi/core/react/utilityContext';
import type { ExportPetitionContentType, ExportPetitionFormat } from '@change-corgi/middle-end-gql-schema';

import { is24HoursAfter } from 'src/app/shared/utils/dates';

import { EXPORT_ERROR_MAP } from '../../constants';

import { StarterDashboardV2ExportPetition as ExportPetitionMutation } from './exportSupporters.graphql';
import type {
	StarterDashboardV2ExportPetitionMutation,
	StarterDashboardV2ExportPetitionMutationVariables,
} from './exportSupporters.graphql';

type ExportPetitionParams = {
	id: string;
	type: ExportPetitionContentType;
	format: ExportPetitionFormat;
	locale: Locale;
	createdAt: string;
};
type ExportPetitionReturn = {
	jobId?: NonNullable<StarterDashboardV2ExportPetitionMutation['exportPetition']['exportJob']>['id'];
};

export async function exportPetition(
	{ id, type, format, locale, createdAt }: ExportPetitionParams,
	{ gql: { fetch }, errorReporter }: UtilityContext,
): Promise<ExportPetitionReturn> {
	if (!is24HoursAfter(new Date(), new Date(createdAt))) {
		throw new Error(EXPORT_ERROR_MAP.PUBLISH_TOO_RECENT.code);
	}

	const { data, errors, requestId } = await fetch<
		StarterDashboardV2ExportPetitionMutation,
		StarterDashboardV2ExportPetitionMutationVariables
	>({
		query: ExportPetitionMutation,
		variables: {
			input: {
				petitionId: id,
				contentType: type,
				format,
				locale,
			},
		},
		rejectOnError: false,
	});

	// graphql errors are reported internally
	if (errors) {
		throw new Error(EXPORT_ERROR_MAP.GENERIC_ERROR.code);
	}

	// report unexpected errors from missing data
	if (!data) {
		const err = new Error(EXPORT_ERROR_MAP.NO_DATA.code);
		void errorReporter.report({
			error: new Error('Failed to export petition'),
			context: {
				requestId,
				reason: EXPORT_ERROR_MAP.NO_DATA.code,
			},
		});
		throw err;
	}

	if (data.exportPetition.status !== 'SUCCESS') {
		const { status } = data.exportPetition;
		throw new Error(EXPORT_ERROR_MAP[status].code);
	}

	return { jobId: data.exportPetition.exportJob?.id };
}
